import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link, useLocation } from "react-router-dom";
import logoIcon from "../assets/images/logo-icon.svg";
import { loginUser } from "../auth";
import { useAuth } from "../contexts/Auth";
import { claimPendingScores } from "../courses";

const Login = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState("");
    const [formError, setFormError] = useState("");
    const [invalidCredentialsError, setInvalidCredentialsError] = useState(false);

    const handleLanguageChange = (e) => {
        i18n.changeLanguage(e.target.value);
    };

    // Update error messages when language changes
    useEffect(() => {
        if (invalidCredentialsError) {
            setFormError(t("auth.invalidCredentials"));
        }
    }, [i18n.language, invalidCredentialsError, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setEmailError("");
        setFormError("");
        setInvalidCredentialsError(false);

        try {
            const data = await loginUser(email, password);
            login(data.access, data.refresh);

            // If they practiced a shared course while logged out, save those
            // scores now and send them straight to that course.
            const claimedCourseId = await claimPendingScores();
            if (claimedCourseId) {
                navigate(`/courses/${claimedCourseId}`, { replace: true });
                return;
            }

            // Redirect to the page they were trying to access, or dashboard as default
            const from = location.state?.from?.pathname || "/dashboard";
            navigate(from, { replace: true });
        } catch (error) {
            if (error.fieldErrors) {
                if (error.fieldErrors.email) {
                    setEmailError(error.fieldErrors.email[0]);
                }
                if (error.fieldErrors.non_field_errors) {
                    const errorMessage = error.fieldErrors.non_field_errors[0];
                    if (errorMessage.includes("Invalid email or password")) {
                        setInvalidCredentialsError(true);
                        setFormError(t("auth.invalidCredentials"));
                    } else {
                        setFormError(errorMessage);
                    }
                }
            } else {
                setFormError(error.message || "Login failed");
            }
        }
    };

    return (
        <div className="flex justify-center bg-white pt-8 sm:pt-16 px-2 sm:p-2">
            <div className="w-full max-w-md space-y-4 sm:space-y-6 p-4 sm:p-8 border border-gray-200 rounded-xl shadow-sm relative">
                {/* Close button */}
                <button
                    type="button"
                    onClick={() => navigate("/")}
                    aria-label={t("auth.close")}
                    title={t("auth.close")}
                    className="absolute top-4 left-4 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1.5 transition-colors"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2}
                        className="h-5 w-5"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                </button>
                {/* Language Switcher */}
                <div className="absolute top-4 right-4 flex items-center gap-2">
                    <span
                        className={`cursor-pointer ${
                            i18n.language === "en" ? "font-bold" : ""
                        } hover-underline underline-black`}
                        onClick={() => handleLanguageChange({ target: { value: "en" } })}
                    >
                        EN
                    </span>
                    <span>|</span>
                    <span
                        className={`cursor-pointer ${
                            i18n.language === "de" ? "font-bold" : ""
                        } hover-underline underline-black`}
                        onClick={() => handleLanguageChange({ target: { value: "de" } })}
                    >
                        DE
                    </span>
                </div>
                <div className="flex justify-center">
                    <Link to="/">
                        <img src={logoIcon} alt="AusspracheTrainer Logo" className="h-24 w-24" />
                    </Link>
                </div>
                <h2 className="text-center text-2xl text-gray-900">{t("auth.welcomeBack")}</h2>
                <p className="text-center text-sm text-gray-500">{t("auth.loginDetails")}</p>
                {/* Display general form error */}
                {formError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {formError}
                    </div>
                )}
                <form className="space-y-4" onSubmit={handleSubmit}>
                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-gray-700 mb-1"
                        >
                            {t("auth.email")}
                        </label>
                        <input
                            id="email"
                            type="email"
                            placeholder={t("auth.emailPlaceholder")}
                            className={`w-full px-4 py-2 border ${
                                emailError ? "border-red-500" : "border-gray-300"
                            } rounded-lg text-sm`}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setEmailError("");
                                setInvalidCredentialsError(false);
                                setFormError("");
                            }}
                            required
                        />
                        {emailError && <p className="mt-1 text-sm text-red-600">{emailError}</p>}
                    </div>
                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label
                                htmlFor="password"
                                className="block text-sm font-medium text-gray-700"
                            >
                                {t("auth.password")}
                            </label>
                            <a
                                href="/forgot-password"
                                className="text-sm text-purple-500 hover:underline"
                            >
                                {t("auth.forgotPassword")}
                            </a>
                        </div>
                        <div className="relative">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                placeholder="••••••••"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md p-1"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                                        />
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        strokeWidth={1.5}
                                        stroke="currentColor"
                                        className="size-4"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
                                        />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </div>
                    {/* Remember me feature (currently auto-active)
                    <div className="flex items-center space-x-2">
                        <input type="checkbox" id="remember" className="w-4 h-4" />
                        <label htmlFor="remember" className="text-sm text-gray-700">
                            {t("auth.rememberMe")}
                        </label>
                    </div>
                    */}
                    <button
                        type="submit"
                        className="w-full bg-primary hover:bg-purple-500 text-white py-2 rounded-md"
                    >
                        {t("auth.login")}
                    </button>
                </form>
                <p className="text-center text-sm text-gray-500">
                    {t("auth.noAccount")}{" "}
                    <Link
                        to="/register"
                        state={{ signup_source: "login-register-instead" }}
                        className="text-purple-500 hover:underline"
                    >
                        {t("auth.register")}
                    </Link>
                </p>
            </div>
        </div>
    );
};

export default Login;
